import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  Grid,
  Card,
  CardContent,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Print as PrintIcon,
  Source as SourceIcon,
} from '@mui/icons-material';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import StatusBadge from 'src/components/TransferOrder/StatusBadge';
import ProgressBar from 'src/components/TransferOrder/ProgressBar';
import Loader from 'src/components/Loader';
import {
  TransferOrder,
  TransferOrderStatus,
  TransferOrderRecord,
  getRecordStatusLabel,
} from 'src/types/TransferOrder.types';
import { TransferOrderService } from 'src/services/transfer-order-service';
import { toast } from 'react-toastify';

const TransferOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentOrder, setCurrentOrder] = useState<TransferOrder | null>(null);
  const [loading, setLoading] = useState(false);

  // Source drawers
  const [sourceDrawerOpen, setSourceDrawerOpen] = useState(false);
  const [overviewDrawerOpen, setOverviewDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<TransferOrderRecord | null>(null);

  const fetchOrder = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const order = await TransferOrderService.getById(id);
      setCurrentOrder(order);
    } catch (error: any) {
      console.error('Failed to fetch transfer order:', error);
      const errorMessage = error?.message || 'Failed to load transfer order';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const openSourceDrawer = (record: TransferOrderRecord) => {
    setSelectedRecord(record);
    setSourceDrawerOpen(true);
  };

  const closeSourceDrawer = () => {
    setSelectedRecord(null);
    setSourceDrawerOpen(false);
  };

  const hasRecords = !!currentOrder?.Records?.length;
  const hasItemsOnly = !currentOrder?.Records && !!currentOrder?.Items?.length;

  // Group sources for overview
  const sourcesGrouped = useMemo(() => {
    if (!currentOrder?.Records) return {};
    const map: Record<
      string,
      {
        sourceStorageUnitName: string;
        boxRefNumbers: Set<string>;
        records: TransferOrderRecord[];
      }
    > = {};
    for (const r of currentOrder.Records) {
      const key = r.sourceStorageUnitName || 'Unassigned';
      if (!map[key]) {
        map[key] = {
          sourceStorageUnitName: key,
          boxRefNumbers: new Set<string>(),
          records: [],
        };
      }
      if (r.boxRefNumber) map[key].boxRefNumbers.add(r.boxRefNumber);
      map[key].records.push(r);
    }
    return map;
  }, [currentOrder]);

  const getActionButton = () => {
    if (!currentOrder) return null;

    switch (currentOrder.Status) {
      case TransferOrderStatus.New:
        return (
          <Button
            variant="contained"
            startIcon={<AssignmentIcon />}
            onClick={() => navigate(`/transfer-orders/${id}/allocate`)}
            size="large"
          >
            Allocate Items
          </Button>
        );
      case TransferOrderStatus.Allocated:
      case TransferOrderStatus.Picking:
        return (
          <Button
            variant="contained"
            startIcon={<InventoryIcon />}
            onClick={() => navigate(`/transfer-orders/${id}/pick`)}
            size="large"
            color="warning"
          >
            {currentOrder.Status === TransferOrderStatus.Allocated ? 'Start Picking' : 'Continue Picking'}
          </Button>
        );
      case TransferOrderStatus.Ready:
        return (
          <Button
            variant="contained"
            startIcon={<LocalShippingIcon />}
            onClick={() => navigate(`/transfer-orders/${id}/ship`)}
            size="large"
            color="info"
          >
            Ship Order
          </Button>
        );
      case TransferOrderStatus.InTransit:
      case TransferOrderStatus.Received:
        return (
          <Alert severity="info" sx={{ flex: 1 }}>
            Order is in transit. Client will receive and confirm delivery.
          </Alert>
        );
      case TransferOrderStatus.Completed:
        return (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            Order completed successfully
          </Alert>
        );
      default:
        return null;
    }
  };

  // ----- PRINT -----
  const handlePrint = () => {
    if (!currentOrder) return;

    const title = currentOrder.OrderName || `Order #${currentOrder.Id.slice(0, 8)}`;
    const dateFmt = (d?: string) => (d ? new Date(d).toLocaleString() : '-');

    // Build rows (records fallback to items)
    const recordRows = currentOrder.Records?.map(
      (r) => `
      <tr>
        <td>${r.barcode || ''}</td>
        <td style="text-align:center">${r.requestedQuantity ?? ''}</td>
        <td style="text-align:center">${r.allocatedQuantity ?? ''}</td>
        <td style="text-align:center">${r.pickedQuantity ?? ''}</td>
        <td style="text-align:center">${r.shippedQuantity ?? ''}</td>
        <td>${r.sourceStorageUnitName ?? ''}</td>
        <td>${r.boxRefNumber ?? ''}</td>
        <td>${r.notes ?? ''}</td>
      </tr>`
    ).join('') ||
    currentOrder.Items?.map(
      (i) => `
      <tr>
        <td>${i.barcode || ''}</td>
        <td style="text-align:center">${i.requestedQuantity ?? ''}</td>
        <td style="text-align:center">-</td>
        <td style="text-align:center">-</td>
        <td style="text-align:center">-</td>
        <td>-</td>
        <td>-</td>
        <td>Pending allocation</td>
      </tr>`
    ).join('') ||
    '';

    // Sources Overview Table
    const sourcesTable = Object.values(sourcesGrouped).length
      ? `
      <h3>Sources Overview</h3>
      <table>
        <thead>
          <tr>
            <th>Source</th>
            <th>Items (barcodes)</th>
            <th>Boxes</th>
          </tr>
        </thead>
        <tbody>
          ${Object.values(sourcesGrouped)
            .map(
              (g) => `
            <tr>
              <td>${g.sourceStorageUnitName}</td>
              <td>${g.records.map((r) => r.barcode).join(', ')}</td>
              <td>${Array.from(g.boxRefNumbers).join(', ') || '-'}</td>
            </tr>`
            )
            .join('')}
        </tbody>
      </table>`
      : '';

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title} – Print</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; margin: 24px; }
    h1 { margin: 0 0 8px; }
    h2 { margin: 24px 0 12px; }
    h3 { margin: 20px 0 8px; }
    .meta { color: #555; margin-bottom: 16px; }
    .grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
    .card { border: 1px solid #ddd; border-radius: 8px; padding: 12px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    th, td { border: 1px solid #ddd; padding: 8px; font-size: 13px; }
    th { text-align: left; background: #f7f7f7; }
    @media print {
      .no-print { display: none; }
    }
    .actions { margin: 12px 0 24px; }
  </style>
</head>
<body>
  <div class="actions no-print">
    <button onclick="window.print()">Print</button>
  </div>

  <h1>${title}</h1>
  <div class="meta">
    <div><strong>Client:</strong> ${currentOrder.ClientName || '-'}</div>
    <div><strong>Status:</strong> ${TransferOrderStatus[currentOrder.Status]}</div>
    <div><strong>Created:</strong> ${dateFmt(currentOrder.CreatedAt)}</div>
    <div><strong>Updated:</strong> ${dateFmt(currentOrder.UpdatedAt)}</div>
  </div>

  <div class="grid">
    <div class="card"><div>Items</div><div style="font-weight:700;font-size:18px">${currentOrder.ItemCount || currentOrder.Records?.length || 0}</div></div>
    <div class="card"><div>Total Quantity</div><div style="font-weight:700;font-size:18px">${currentOrder.TotalRequestedQuantity || currentOrder.TotalQuantity || 0}</div></div>
    <div class="card"><div>Total Allocated</div><div style="font-weight:700;font-size:18px">${currentOrder.TotalAllocatedQuantity ?? 0}</div></div>
    <div class="card"><div>Total Picked</div><div style="font-weight:700;font-size:18px">${currentOrder.TotalPickedQuantity ?? 0}</div></div>
  </div>

  ${sourcesTable}

  <h2>Items</h2>
  <table>
    <thead>
      <tr>
        <th>Barcode</th>
        <th>Requested</th>
        <th>Allocated</th>
        <th>Picked</th>
        <th>Shipped</th>
        <th>Source</th>
        <th>Box Ref</th>
        <th>Notes</th>
      </tr>
    </thead>
    <tbody>
      ${recordRows}
    </tbody>
  </table>

  ${currentOrder.Notes ? `<h2>Notes</h2><p>${currentOrder.Notes}</p>` : ''}

  <script>window.focus();</script>
</body>
</html>
    `;

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const calculatePhaseProgress = (phase: 'allocation' | 'picking' | 'shipping' | 'receiving'): number => {
    if (!currentOrder) return 0;
    const total = currentOrder.TotalRequestedQuantity || currentOrder.TotalQuantity || 0;
    if (total === 0) return 0;

    switch (phase) {
      case 'allocation':
        return ((currentOrder.TotalAllocatedQuantity || 0) / total) * 100;
      case 'picking': {
        const allocated = currentOrder.TotalAllocatedQuantity || 0;
        if (allocated === 0) return 0;
        return ((currentOrder.TotalPickedQuantity || 0) / allocated) * 100;
      }
      case 'shipping': {
        const picked = currentOrder.TotalPickedQuantity || 0;
        if (picked === 0) return 0;
        return ((currentOrder.TotalShippedQuantity || 0) / picked) * 100;
      }
      case 'receiving': {
        const shipped = currentOrder.TotalShippedQuantity || 0;
        if (shipped === 0) return 0;
        return ((currentOrder.TotalReceivedQuantity || 0) / shipped) * 100;
      }
      default:
        return 0;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Loader />
      </Box>
    );
  }

  if (!currentOrder) {
    return (
      <Box>
        <Alert severity="error">Transfer order not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/transfer-order')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/transfer-order')}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h4" fontWeight={700}>
            {currentOrder.OrderName || `Order #${currentOrder.Id.slice(0, 8)}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Client: {currentOrder.ClientName}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Print order and sources">
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              size="small"
            >
              Print
            </Button>
          </Tooltip>
          <Tooltip title="View all sources overview">
            <Button
              variant="outlined"
              startIcon={<SourceIcon />}
              onClick={() => setOverviewDrawerOpen(true)}
              size="small"
            >
              Sources
            </Button>
          </Tooltip>
          <StatusBadge status={currentOrder.Status} type="order" size="medium" />
        </Stack>
      </Stack>

      {/* Action Button */}
      <Box sx={{ mb: 3 }}>{getActionButton()}</Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Items
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {currentOrder.ItemCount || currentOrder.Records?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Quantity
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {currentOrder.TotalRequestedQuantity || currentOrder.TotalQuantity || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Created
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {currentOrder.CreatedAt ? new Date(currentOrder.CreatedAt).toLocaleDateString() : '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Last Updated
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {currentOrder.UpdatedAt ? new Date(currentOrder.UpdatedAt).toLocaleDateString() : '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Progress Phases */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Order Progress
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Allocation
            </Typography>
            <ProgressBar order={currentOrder} phase="allocation" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Picking
            </Typography>
            <ProgressBar order={currentOrder} phase="picking" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Shipping
            </Typography>
            <ProgressBar order={currentOrder} phase="shipping" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Receiving
            </Typography>
            <ProgressBar order={currentOrder} phase="receiving" />
          </Grid>
        </Grid>
      </Paper>

      {/* Items Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Order Items
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={56}>Source</TableCell>
                <TableCell>Barcode</TableCell>
                <TableCell align="center">Requested</TableCell>
                <TableCell align="center">Allocated</TableCell>
                <TableCell align="center">Picked</TableCell>
                <TableCell align="center">Shipped</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasRecords ? (
                currentOrder!.Records!.map((record: TransferOrderRecord) => (
                  <TableRow hover key={record.id}>
                    <TableCell>
                      {record.sourceStorageUnitName ? (
                        <Tooltip title="View source details">
                          <IconButton size="small" onClick={() => openSourceDrawer(record)}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Chip label="-" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                        {record.barcode}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{record.requestedQuantity}</TableCell>
                    <TableCell align="center">{record.allocatedQuantity}</TableCell>
                    <TableCell align="center">{record.pickedQuantity}</TableCell>
                    <TableCell align="center">{record.shippedQuantity}</TableCell>
                    <TableCell>
                      <Chip label={getRecordStatusLabel(record.status)} size="small" variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))
              ) : hasItemsOnly ? (
                currentOrder!.Items!.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip label="Pending" size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                        {item.barcode}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{item.requestedQuantity}</TableCell>
                    <TableCell align="center" colSpan={4}>
                      <Typography variant="caption" color="text.secondary">
                        Pending allocation
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No items in this order
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Notes */}
      {currentOrder.Notes && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentOrder.Notes}
          </Typography>
        </Paper>
      )}

      {/* Drawer: Single Source (record) */}
      <Drawer anchor="right" open={sourceDrawerOpen} onClose={closeSourceDrawer} PaperProps={{ sx: { width: 420 } }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Source Details
            </Typography>
            <Button onClick={closeSourceDrawer}>Close</Button>
          </Stack>
          {selectedRecord ? (
            <List dense>
              <ListItem>
                <ListItemText primary="Barcode" secondary={selectedRecord.barcode} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Source Location" secondary={selectedRecord.sourceStorageUnitName || '-'} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Box Reference" secondary={selectedRecord.boxRefNumber || '-'} />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Qty (Req/Alloc/Pick/Ship)"
                  secondary={`${selectedRecord.requestedQuantity ?? 0} / ${selectedRecord.allocatedQuantity ?? 0} / ${selectedRecord.pickedQuantity ?? 0} / ${selectedRecord.shippedQuantity ?? 0}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Status" secondary={getRecordStatusLabel(selectedRecord.status)} />
              </ListItem>
              {selectedRecord.notes && (
                <ListItem>
                  <ListItemText primary="Notes" secondary={selectedRecord.notes} />
                </ListItem>
              )}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No record selected.
            </Typography>
          )}
        </Box>
      </Drawer>

      {/* Drawer: Sources Overview */}
      <Drawer
        anchor="right"
        open={overviewDrawerOpen}
        onClose={() => setOverviewDrawerOpen(false)}
        PaperProps={{ sx: { width: 520 } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Sources Overview
            </Typography>
            <Button onClick={() => setOverviewDrawerOpen(false)}>Close</Button>
          </Stack>

          {Object.keys(sourcesGrouped).length ? (
            <List dense>
              {Object.values(sourcesGrouped).map((g) => (
                <Box key={g.sourceStorageUnitName} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {g.sourceStorageUnitName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Boxes: {Array.from(g.boxRefNumbers).join(', ') || '-'}
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {g.records.map((r) => (
                      <ListItem key={r.id} sx={{ px: 2 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                              {r.barcode}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              Qty Req/Alloc/Pick/Ship: {r.requestedQuantity ?? 0}/{r.allocatedQuantity ?? 0}/{r.pickedQuantity ?? 0}/{r.shippedQuantity ?? 0}
                            </Typography>
                          }
                        />
                        <Chip label={getRecordStatusLabel(r.status)} size="small" variant="outlined" />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No sources to display.
            </Typography>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default TransferOrderDetail;
